import { getProjectBySlug, bio, socialLinks } from '@/data/projects'
import type { Project } from '@/types'

export interface PageMeta {
  title: string
  description: string
  ogTitle: string
  ogDescription: string
  ogImage: string
  ogType: string
  canonicalPath: string
  robots: string
  articleAuthor?: string
  articlePublishedTime?: string
  articleTags?: string[]
}

const BASE_URL = 'https://nataliatarasova.info'
const DEFAULT_OG_IMAGE = `${BASE_URL}/og-image.jpg`
const SITE_DESCRIPTION =
  'Lead Motion Designer in Brooklyn, NY specializing in product animation systems, Lottie, design tokens, and AI character design for Vodafone, HBO Max, and Bumble.'
const INDEXABLE_ROBOTS =
  'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
const NOINDEX_ROBOTS = 'noindex, follow'

const defaultMeta: PageMeta = {
  title: `${bio.name} - ${bio.role}`,
  description: SITE_DESCRIPTION,
  ogTitle: `${bio.name} - ${bio.role}`,
  ogDescription: SITE_DESCRIPTION,
  ogImage: DEFAULT_OG_IMAGE,
  ogType: 'website',
  canonicalPath: '/',
  robots: INDEXABLE_ROBOTS,
}

const notFoundMeta: PageMeta = {
  title: `Page not found - ${bio.name}`,
  description: 'The page you are looking for could not be found.',
  ogTitle: `Page not found - ${bio.name}`,
  ogDescription: 'The page you are looking for could not be found.',
  ogImage: DEFAULT_OG_IMAGE,
  ogType: 'website',
  canonicalPath: '/404',
  robots: NOINDEX_ROBOTS,
}

function parseProjectDate(date: string): { start: string; end: string } {
  const parts = date.split(/\s*[-–]\s*/).map((p) => p.trim())
  const startYear = parts[0]?.match(/\d{4}/)?.[0] ?? String(new Date().getFullYear())
  const endYear = parts[1]?.match(/\d{4}/)?.[0] ?? startYear
  return { start: `${startYear}-01-01`, end: `${endYear}-12-31` }
}

function projectOgImagePath(slug: string): string {
  return `${BASE_URL}/og/${slug}.jpg`
}

function extractProjectSlug(pathname: string): string | null {
  const match =
    pathname.match(/^\/project\/([^/]+)\/?$/) ||
    pathname.match(/^\/project-detail-([^/]+)\/?$/) ||
    pathname.match(/^\/project-detail\/([^/]+)\/?$/)
  return match ? match[1] : null
}

function projectMeta(project: Project): PageMeta {
  const description = project.description.slice(0, 160)
  const { start } = parseProjectDate(project.date)
  const robots = project.comingSoon ? NOINDEX_ROBOTS : INDEXABLE_ROBOTS

  return {
    title: `${project.name} - ${bio.name}`,
    description,
    ogTitle: `${project.name} - ${bio.name}`,
    ogDescription: description,
    ogImage: projectOgImagePath(project.slug),
    ogType: 'article',
    canonicalPath: `/project/${project.slug}`,
    robots,
    articleAuthor: bio.name,
    articlePublishedTime: start,
    articleTags: project.tags,
  }
}

export function getPageMeta(pathname: string): PageMeta {
  if (pathname === '/') return defaultMeta
  if (pathname === '/404') return notFoundMeta

  const slug = extractProjectSlug(pathname)
  if (slug) {
    const project = getProjectBySlug(slug)
    if (project) return projectMeta(project)
  }

  return notFoundMeta
}

export function getCanonicalUrl(path: string): string {
  return `${BASE_URL}${path}`
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function personSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: bio.name,
    jobTitle: bio.role,
    description: bio.summary,
    email: `mailto:${bio.email}`,
    url: BASE_URL,
    image: DEFAULT_OG_IMAGE,
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Brooklyn',
      addressRegion: 'NY',
      addressCountry: 'US',
    },
    sameAs: socialLinks.map((s) => s.url),
  }
}

function creativeWorkSchema(project: Project) {
  const { start } = parseProjectDate(project.date)
  return {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name: project.name,
    headline: project.name,
    description: project.description,
    image: projectOgImagePath(project.slug),
    url: `${BASE_URL}/project/${project.slug}`,
    datePublished: start,
    keywords: project.tags.join(', '),
    about: project.tags,
    author: {
      '@type': 'Person',
      name: bio.name,
      url: BASE_URL,
    },
    creator: {
      '@type': 'Person',
      name: bio.name,
      url: BASE_URL,
    },
  }
}

function breadcrumbSchema(project: Project) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: BASE_URL },
      { '@type': 'ListItem', position: 2, name: 'Work', item: `${BASE_URL}/#work` },
      {
        '@type': 'ListItem',
        position: 3,
        name: project.name,
        item: `${BASE_URL}/project/${project.slug}`,
      },
    ],
  }
}

function videoSchema(project: Project, videoId: string) {
  const { start } = parseProjectDate(project.date)
  return {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: `${project.name} — ${bio.name}`,
    description: project.description.slice(0, 200),
    thumbnailUrl: projectOgImagePath(project.slug),
    embedUrl: `https://player.vimeo.com/video/${videoId}`,
    contentUrl: `https://vimeo.com/${videoId}`,
    uploadDate: start,
    author: { '@type': 'Person', name: bio.name, url: BASE_URL },
  }
}

export function generateJsonLd(pathname: string): object[] {
  if (pathname === '/') {
    return [personSchema()]
  }

  const slug = extractProjectSlug(pathname)
  if (slug) {
    const project = getProjectBySlug(slug)
    if (project) {
      const blocks: object[] = [creativeWorkSchema(project), breadcrumbSchema(project)]
      for (const id of project.videoIds) {
        blocks.push(videoSchema(project, id))
      }
      return blocks
    }
  }

  return []
}

function serializeJsonLd(blocks: object[]): string {
  const payload = blocks.length === 1 ? blocks[0] : blocks
  return JSON.stringify(payload).replace(/</g, '\\u003c')
}

export function generateHeadTags(pathname: string): string {
  const meta = getPageMeta(pathname)
  const canonicalUrl = getCanonicalUrl(meta.canonicalPath)
  const jsonLd = generateJsonLd(pathname)

  const lines = [
    `<title>${escapeHtml(meta.title)}</title>`,
    `<meta name="description" content="${escapeHtml(meta.description)}" />`,
    `<meta name="author" content="${escapeHtml(bio.name)}" />`,
    `<meta name="robots" content="${meta.robots}" />`,
    `<link rel="canonical" href="${canonicalUrl}" />`,
    `<meta property="og:type" content="${meta.ogType}" />`,
    `<meta property="og:site_name" content="${escapeHtml(bio.name)}" />`,
    `<meta property="og:locale" content="en_US" />`,
    `<meta property="og:title" content="${escapeHtml(meta.ogTitle)}" />`,
    `<meta property="og:description" content="${escapeHtml(meta.ogDescription)}" />`,
    `<meta property="og:image" content="${meta.ogImage}" />`,
    `<meta property="og:image:width" content="1200" />`,
    `<meta property="og:image:height" content="630" />`,
    `<meta property="og:url" content="${canonicalUrl}" />`,
    `<meta name="twitter:card" content="summary_large_image" />`,
    `<meta name="twitter:title" content="${escapeHtml(meta.ogTitle)}" />`,
    `<meta name="twitter:description" content="${escapeHtml(meta.ogDescription)}" />`,
    `<meta name="twitter:image" content="${meta.ogImage}" />`,
  ]

  if (meta.articleAuthor) {
    lines.push(`<meta property="article:author" content="${escapeHtml(meta.articleAuthor)}" />`)
  }
  if (meta.articlePublishedTime) {
    lines.push(`<meta property="article:published_time" content="${meta.articlePublishedTime}" />`)
  }
  if (meta.articleTags) {
    for (const tag of meta.articleTags) {
      lines.push(`<meta property="article:tag" content="${escapeHtml(tag)}" />`)
    }
  }

  if (jsonLd.length > 0) {
    lines.push(`<script type="application/ld+json">${serializeJsonLd(jsonLd)}</script>`)
  }

  return lines.join('\n    ')
}
